import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import AffiliateDashboard from './pages/affiliate/Dashboard';
import AffiliateAssets from './pages/affiliate/Assets';
import AffiliateBilling from './pages/affiliate/Billing';
import AffiliateCreatives from './pages/affiliate/Creatives';
import AffiliatePostbacks from './pages/affiliate/Postbacks';
import AdminDashboard from './pages/admin/Dashboard';
import AdminInvoices from './pages/admin/Invoices';
import AdminUsers from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminSandbox from './pages/admin/Sandbox';
import AdminClicks from './pages/admin/Clicks';
import AdminConversions from './pages/admin/Conversions';
import AdminPostbackLogs from './pages/admin/PostbackLogs';
import AdminGlobalPostbacks from './pages/admin/GlobalPostbacks';
import TeamManagement from './pages/admin/TeamManagement';
import Settings from './pages/Settings';
import { PermissionsGate } from './components/common/PermissionsGate';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';

// Loading spinner component
function LoadingScreen() {
    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-400">Loading...</p>
            </div>
        </div>
    );
}

function NotFoundPage() {
    const { user } = useAuth();
    const location = useLocation();
    const homePath = user ? (user.role === 'admin' ? '/admin' : '/dashboard') : '/login';

    return (
        <div className="min-h-screen flex items-center justify-center px-6">
            <div className="max-w-lg text-center space-y-6">
                <div className="space-y-2">
                    <p className="text-sm uppercase tracking-[0.3em] text-gray-500">404</p>
                    <h1 className="text-4xl font-bold text-white">Page not found</h1>
                    <p className="text-gray-400">
                        No route matched <span className="font-mono text-gray-300">{location.pathname}</span>.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to={homePath} className="btn-primary">
                        {user ? 'Go to dashboard' : 'Go to login'}
                    </Link>
                    {!user && (
                        <Link to="/register" className="btn-secondary">
                            Open register page
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

// Protected route component
function ProtectedRoute({
    children,
    requiredRole
}: {
    children: React.ReactNode;
    requiredRole?: 'admin' | 'affiliate';
}) {
    const { user, loading } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requiredRole && user.role !== requiredRole) {
        // Redirect to appropriate dashboard based on role
        return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    }

    return <>{children}</>;
}

// Public route (redirect if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    if (user) {
        return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    }

    return <>{children}</>;
}

function AdminPageRoute({
    children,
    permission
}: {
    children: React.ReactNode;
    permission: string;
}) {
    return (
        <ProtectedRoute requiredRole="admin">
            <DashboardLayout>
                <PermissionsGate permission={permission} fallback={<div className="p-8 text-center text-red-500">Access Denied</div>}>
                    {children}
                </PermissionsGate>
            </DashboardLayout>
        </ProtectedRoute>
    );
}

function App() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            <Route path="/register/*" element={
                <PublicRoute>
                    <Register />
                </PublicRoute>
            } />
            <Route path="/reset-password" element={
                <PublicRoute>
                    <ResetPassword />
                </PublicRoute>
            } />

            {/* Shared Protected Routes */}
            <Route path="/settings" element={
                <ProtectedRoute>
                    <DashboardLayout>
                        <Settings />
                    </DashboardLayout>
                </ProtectedRoute>
            } />

            {/* Affiliate Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute requiredRole="affiliate">
                    <DashboardLayout>
                        <AffiliateDashboard />
                    </DashboardLayout>
                </ProtectedRoute>
            } />
            <Route path="/assets" element={
                <ProtectedRoute requiredRole="affiliate">
                    <DashboardLayout>
                        <AffiliateAssets />
                    </DashboardLayout>
                </ProtectedRoute>
            } />
            <Route path="/billing" element={
                <ProtectedRoute requiredRole="affiliate">
                    <DashboardLayout>
                        <AffiliateBilling />
                    </DashboardLayout>
                </ProtectedRoute>
            } />
            <Route path="/creatives" element={
                <ProtectedRoute requiredRole="affiliate">
                    <DashboardLayout>
                        <AffiliateCreatives />
                    </DashboardLayout>
                </ProtectedRoute>
            } />
            <Route path="/postbacks" element={
                <ProtectedRoute requiredRole="affiliate">
                    <DashboardLayout>
                        <AffiliatePostbacks />
                    </DashboardLayout>
                </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
                <AdminPageRoute permission="view_analytics">
                    <AdminDashboard />
                </AdminPageRoute>
            } />
            <Route path="/admin/invoices" element={
                <AdminPageRoute permission="manage_finance">
                    <AdminInvoices />
                </AdminPageRoute>
            } />
            <Route path="/admin/users" element={
                <AdminPageRoute permission="manage_users">
                    <AdminUsers />
                </AdminPageRoute>
            } />
            <Route path="/admin/products" element={
                <AdminPageRoute permission="manage_settings">
                    <AdminProducts />
                </AdminPageRoute>
            } />
            <Route path="/admin/sandbox" element={
                <AdminPageRoute permission="manage_settings">
                    <AdminSandbox />
                </AdminPageRoute>
            } />
            <Route path="/admin/clicks" element={
                <AdminPageRoute permission="view_analytics">
                    <AdminClicks />
                </AdminPageRoute>
            } />
            <Route path="/admin/conversions" element={
                <AdminPageRoute permission="view_analytics">
                    <AdminConversions />
                </AdminPageRoute>
            } />
            <Route path="/admin/postback-logs" element={
                <AdminPageRoute permission="view_analytics">
                    <AdminPostbackLogs />
                </AdminPageRoute>
            } />
            <Route path="/admin/global-postbacks" element={
                <AdminPageRoute permission="manage_settings">
                    <AdminGlobalPostbacks />
                </AdminPageRoute>
            } />
            <Route path="/admin/team" element={
                <AdminPageRoute permission="manage_users">
                    <TeamManagement />
                </AdminPageRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}

export default App;
